import React, { useContext, useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import styles from "./CurrentAgendaItemVotings.module.css";

const CurrentAgendaItemVotingsBase = () => {
    const { t } = useTranslation();
    const { uiStore, agendaitemStore, votingStore, assemblyStore } = useContext(RootStoreContext);

    const [showDeleteVotingModal, setShowDeleteVotingModal] = useState<boolean>(false);
    const [deleteVotingId, setDeleteVotingId] = useState<string>("");

    useEffect(() => {
        if (agendaitemStore.currentAgendaItem?.id) {
            votingStore.loadAllVotingsByAgendaItemId(agendaitemStore.currentAgendaItem.id);
        }
    }, [agendaitemStore.currentAgendaItem, votingStore]);

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const onClickTashIcon = useCallback(
        (votingid: string) => async (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();
            setShowDeleteVotingModal(true);
            setDeleteVotingId(votingid);
        },
        []
    );

    const onClickVoting = useCallback(
        (votingid: string) => async (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();
            const voting = votingStore.votingList.find((v) => v.votingid === votingid);
            if (!voting) return;

            const agendaItemId = agendaitemStore.currentAgendaItem?.id;
            if (!agendaItemId) return;

            votingStore.setSelectedVoting(voting);
            votingStore.setSelectedVotingId(votingid);
            votingStore.updateVotingBasisField("votingId", votingid);
            votingStore.setIsNewVoting(false);

            // Load all edit data before opening the modal so the form is
            // fully populated and votingMlsId is known before any save runs.
            await votingStore.loadVotingInformationBasis();
            await Promise.all([votingStore.loadVotingDetails(votingid, agendaItemId), votingStore.loadVotingOptions()]);

            votingStore.setIsVotingModalDisplayed(true);
        },
        [votingStore, agendaitemStore]
    );

    const onDeleteVoting = useCallback(
        () => async (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();
            if (votingStore.votingList.length > 0) {
                await votingStore.deleteVotingById(deleteVotingId);
                printStatusMessage(t("screens.assemblies.vote.voting_delete_success"), MessageType.SUCCESS);
                setShowDeleteVotingModal(false);
                await agendaitemStore.loadAgendaItems();
            }
        },
        [deleteVotingId, votingStore, printStatusMessage, t, agendaitemStore]
    );

    const onCloseModal = useCallback(async () => {
        setShowDeleteVotingModal(false);
    }, []);

    if (votingStore.votingList.length <= 0) {
        return null;
    }

    return (
        <div className="mb-4">
            {votingStore.votingList.map((voting) => {
                return (
                    <div className={styles.ListItem} key={voting.votingid}>
                        <div className={styles.VotingItemsContainer} onClick={onClickVoting(voting.votingid)}>
                            <UI.Icon icon={UI.SVGIcon.Voting} size={28} className={styles.VotingIcon} />
                            <div className={styles.VotingDetailsRow}>
                                <div className={styles.VotingTitleQuestion}>
                                    {voting.title}: {voting.question}
                                </div>
                            </div>
                        </div>
                        {!assemblyStore.isAssemblyReadOnly && (
                            <div className={styles.AdditionalIcon}>
                                <UI.Icon
                                    className={styles.TrashIcon}
                                    icon={UI.SVGIcon.Trash}
                                    onClick={onClickTashIcon(voting.votingid)}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
            <UI.Modal show={showDeleteVotingModal} onClose={onCloseModal} title={t("labels.delete").toString()}>
                <div>{t("screens.assemblies.vote.voting_delete_question")}</div>
                <div className={styles.ButtonRow}>
                    <UI.Button
                        label={t("labels.yes")}
                        onClick={onDeleteVoting()}
                        className={styles.ButtonModal}
                        variant="danger"
                    />
                    <UI.Button
                        label={t("labels.no")}
                        onClick={onCloseModal}
                        className={styles.ButtonModal}
                        variant="danger"
                    />
                </div>
            </UI.Modal>
        </div>
    );
};

export const CurrentAgendaItemVotings = Sentry.withProfiler(observer(CurrentAgendaItemVotingsBase));
